import { FC, useEffect } from "react";
import { useRouter } from "next/router";
import HomeHeader from "../homepage/HomeHeader";

interface NavbarProps {
  searchQuery?: string;
  setSearchQuery?: (newSearchQuery: string) => void;
  zipcode: string;
  setZipcode: (newZipcode: string) => void;
  handleLocationInputClick: () => void;
  isLanding?: boolean;
  handleSubmit: (e, modalZipcode?) => void;
  isLoading?: false;
  googleLoaded?: boolean;
}

interface LoadingNavbarProps {
  searchQuery: string;
  zipcode: string;
  isLoading: true;
  isLanding?: false;
}

const Navbar: FC<NavbarProps | LoadingNavbarProps> = (props) => {
  const router = useRouter();
  // Warm the /search_results JS chunk wherever the search bar is mounted so the
  // navigation after the intent request is instant. Prefetch only loads the
  // chunk; the getServerSideProps data request still runs on navigation. Next
  // skips prefetch in development, and test routers do not define it, hence
  // the optional call wrapped in Promise.resolve.
  useEffect(() => {
    try {
      void Promise.resolve(router.prefetch?.("/search_results")).catch(() => undefined);
    } catch {
      // prefetch is best effort
    }
  }, [router]);

  return (
    <>
      <HomeHeader />
      {!props.isLanding && (
        <div className="border-b border-[#E9EEE7] bg-[#FFFDF9] px-4 py-3">
          {!props.isLoading
            ? <SearchBar {...(props as NavbarProps)} />
            : <LoadingSearchBar {...props} />}
        </div>
      )}
    </>
  );
};

const SearchBar: FC<NavbarProps> = ({
  searchQuery,
  setSearchQuery,
  handleSubmit,
}) => (
  <div className="mx-auto w-full max-w-[770px]">
    <form
      id="form"
      role="search"
      aria-label="Search groceries again"
      className="flex min-h-[48px] w-full items-center gap-1.5 rounded-full border border-[#D8E0D9] bg-white p-1.5 shadow-[0_5px_18px_rgba(29,60,36,0.07)]"
      onSubmit={handleSubmit}
    >
      <label htmlFor="query" className="sr-only">Search groceries and products</label>
      <input
        type="search"
        id="query"
        name="searched"
        enterKeyHint="search"
        placeholder="Search for another grocery item"
        minLength={3}
        required
        value={searchQuery || ""}
        onInvalid={event => event.currentTarget.setCustomValidity("Enter at least three characters to search.")}
        onChange={event => {
          setSearchQuery?.(event.target.value);
          event.currentTarget.setCustomValidity("");
        }}
        className="h-10 min-w-0 flex-1 rounded-full bg-transparent pl-4 pr-2 font-ui text-sm font-medium text-[#173E2F] outline-none placeholder:text-[#708276] sm:text-base"
      />
      <button
        type="submit"
        id="submit"
        className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-full bg-[#337A4D] px-5 font-ui text-sm font-bold text-white hover:bg-[#28653F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E7AF38]"
      >
        Search
      </button>
    </form>
  </div>
);

const LoadingSearchBar: FC<LoadingNavbarProps> = ({ searchQuery }) => (
  <div className="mx-auto flex max-w-[770px] items-center rounded-full border border-[#D8E0D9] bg-white px-4 py-3">
    <span className="w-full truncate font-ui text-sm text-[#6B7D6E]">
      {searchQuery || "Searching groceries..."}
    </span>
    <span className="ml-3 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#D8E7D9] border-t-[#337A4D]" aria-hidden="true" />
  </div>
);

export default Navbar;
